import { useQuery } from '@tanstack/react-query';

import { definitions } from '@luxuryescapes/contract-svc-cruise';

import useCurrentBrand from '~/hooks/useCurrentBrand';

import CruiseLineService, { type CruiseLine } from '~/services/cruises/CruiseLineService';

type CruiseBrand = definitions['LE_BRAND'];

const CRUISE_BRANDS = new Set<string>([
  'zoomzoom',
  'cudotravel',
  'dealstravel',
  'kogantravel',
  'treatmetravel',
  'scoopontravel',
  'luxuryescapes',
  'lebusinesstraveller',
  'leagenthub',
  'leagenthub_cvfr',
]);

function toCruiseBrand(brand: string): CruiseBrand | undefined {
  return CRUISE_BRANDS.has(brand) ? (brand as CruiseBrand) : undefined;
}

export const cruiseLineKeys = {
  all: ['cruiseLines'] as const,
  list: (brand?: CruiseBrand) => [...cruiseLineKeys.all, 'list', brand] as const,
  details: () => [...cruiseLineKeys.all, 'detail'] as const,
  detail: (id: string) => [...cruiseLineKeys.details(), id] as const,
};

export function useGetCruiseLines() {
  const brand = toCruiseBrand(useCurrentBrand());

  return useQuery<Array<CruiseLine>>({
    queryKey: cruiseLineKeys.list(brand),
    queryFn: async () => {
      const response = await CruiseLineService.listWithPagination({ brand, skip: 0, take: 100 });
      return response.result;
    },
  });
}

export function useGetCruiseLine(id: string | undefined, enabled = true) {
  return useQuery<CruiseLine>({
    queryKey: cruiseLineKeys.detail(id ?? ''),
    queryFn: () => CruiseLineService.getCruiseLineById(id),
    enabled: enabled && !!id,
  });
}
